import { Component, HostBinding, OnInit, OnDestroy } from '@angular/core';
import { MatCheckboxChange } from '@angular/material';

import { combineLatest, Subject } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';

import { ApplicationService, EmailPreferenceOption, EmailPreference, RoleInfo } from 'services/application/application.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { ErrorService } from 'services/error/error.service';
import { ScreenSizeService } from 'services/screensize/screensize.service';
import { SplashService } from 'services/splash/splash.service';
import { UserInfo, UserService } from 'services/user/user.service';

@Component({
    selector: 'app-email-preferences',
    styleUrls: ['emailpreferences.component.scss'],
    templateUrl: 'emailpreferences.component.html',
})
export class EmailPreferencesComponent implements OnInit, OnDestroy {
    @HostBinding('class.smallsize') SmallDisplay: boolean;
    private _subrelease: Subject<void> = new Subject<void>();

    AllRoles: RoleInfo[];
    EmailOptions: EmailPreferenceOption[];
    Preferences: EmailPreference[];
    PreferencesMatrix: MatrixInfo[];
    RoleNames: RoleHeaderInfo[];
    User: UserInfo;
    ViewReady: boolean;

    constructor(
        private appService: ApplicationService,
        private dialogService: DialogService,
        private errorService: ErrorService,
        private screenSizeService: ScreenSizeService,
        private splashService: SplashService,
        private userService: UserService
    ) {}

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.ViewReady = false;

        // Subscribe to changes in the screen size.
        self.screenSizeService.ScreenSizeSub.pipe(takeUntil(self._subrelease)).subscribe(size => {
            self.SmallDisplay = size.IsSmallSize;
        });

        self.SmallDisplay = self.screenSizeService.ScreenSize.IsSmallSize; // Initial setting

        // Wait for the first non-null UserInfo object, to begin component life cycle.
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(_u => _u && !!_u.AuthToken)
        ).subscribe(
            _user => {
                self.User = _user;

                // Set up constant user monitoring
                self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(
                    user => {
                        self.User = user;
                    },
                    error => self.errorService.HandleServiceError(error)
                );

                // Grab the information needed for the component
                combineLatest(
                    self.appService.GetEmailPreferenceOptions(),
                    self.appService.GetEmailPreferences(self.User.AuthToken, self.User.CurrentRole),
                    self.appService.GetRoleList()
                )
                    .pipe(
                        takeUntil(self._subrelease),
                        map(_packet => {
                            return {
                                Options: _packet[0],
                                Preferences: _packet[1],
                                Roles: _packet[2],
                            };
                        })
                    )
                    .subscribe(
                        packet => {
                            self.EmailOptions = packet.Options || [];
                            self.Preferences = packet.Preferences || [];
                            self.AllRoles = (packet.Roles || []).filter(r => r.RoleName !== 'Developer');

                            // Build out the matrix
                            self.BuildPreferencesArray();

                            // The page is ready for viewing.
                            self.ViewReady = true;
                        },
                        error => self.errorService.HandleServiceError(error)
                    );
            },
            error => self.errorService.HandleServiceError(error)
        );
    }

    // #region Build Preferences Array

    BuildPreferencesArray() {
        const self = this;

        if (!self.EmailOptions || !self.EmailOptions.length) {
            return;
        }

        // Set up the role names headers
        const tempNames = <RoleHeaderInfo[]>[];

        (self.AllRoles || []).forEach(r => {
            if (r.RoleName === 'BoardMember' || r.RoleName === 'Cabinet') {
                // For these, we need to add the original and the non-owner versions
                tempNames.push({
                    RoleDescription: `${r.RoleDescription} (Owner)`,
                    ToolTipInfo: `This refers to the ${
                        r.RoleName === 'BoardMember'
                            ? 'originating Board Member who created the request in the system.'
                            : 'cabinet member who is assigned the request by a Facilitator.'
                    }`,
                });
                tempNames.push({
                    RoleDescription: `${r.RoleDescription} (Non-Owner)`,
                    ToolTipInfo: `This refers to any other ${
                        r.RoleName === 'BoardMember'
                            ? 'Board Members than the one who created the request in the system.'
                            : 'cabinet members than the one who is assigned the request by a Facilitator.'
                    }`,
                });
            } else {
                tempNames.push({
                    RoleDescription: r.RoleDescription,
                    ToolTipInfo: '',
                });
            }
        });

        self.RoleNames = tempNames;

        // Build out the matrix of preferences
        const matrix = <MatrixInfo[]>[];

        self.EmailOptions.forEach(opt => {
            // For each option, build out entries for each major role in the system. Specifically segment "Owner" and "Not Owner"
            // for Board Member and Cabinet Member roles.
            const optarr = <EmailPreference[]>[];

            self.AllRoles.forEach(role => {
                const entry = <EmailPreference>{
                    RoleID: role.RoleID,
                    RoleName: role.RoleDescription,
                    EmailPreferenceOptionID: opt.EmailPreferenceOptionID,
                    EventCode: opt.EventCode,
                    InstantEmail: false,
                    DailyRoundupEmail: false,
                    MobileEmail: 'N',
                    NonOwner: false,
                };

                // Do we have a stored value matching this criteria loaded from the database?
                const dbentry = self.Preferences.find(
                    p =>
                        p.EmailPreferenceOptionID === opt.EmailPreferenceOptionID &&
                        p.RoleID === role.RoleID &&
                        p.NonOwner === entry.NonOwner
                );

                if (dbentry) {
                    entry.InstantEmail = dbentry.InstantEmail;
                    entry.DailyRoundupEmail = dbentry.DailyRoundupEmail;

                    // For mobile display, we need to convert the individual bit fields into a combined field.
                    if (entry.InstantEmail && entry.DailyRoundupEmail) {
                        entry.MobileEmail = 'B';
                    } else if (!entry.InstantEmail && !entry.DailyRoundupEmail) {
                        entry.MobileEmail = 'N';
                    } else {
                        entry.MobileEmail = entry.InstantEmail ? 'I' : 'D';
                    }
                    entry.EmailPreferenceID = dbentry.EmailPreferenceID;
                }

                // If this is for a BoardMember or Cabinet, adjust the RoleDescription by adding some text.
                if (role.RoleName === 'BoardMember' || role.RoleName === 'Cabinet') {
                    entry.RoleName = role.RoleDescription + ' (Owner)';
                }

                optarr.push(entry);

                // If this is for a BoardMember or Cabinet, add another entry for the "Not Owner" version
                if (role.RoleName === 'BoardMember' || role.RoleName === 'Cabinet') {
                    const nonentry = <EmailPreference>{
                        RoleID: role.RoleID,
                        RoleName: role.RoleDescription + ' (Non-Owner)',
                        EmailPreferenceOptionID: opt.EmailPreferenceOptionID,
                        EventCode: opt.EventCode,
                        InstantEmail: false,
                        DailyRoundupEmail: false,
                        MobileEmail: 'N',
                        NonOwner: true,
                    };

                    // Do we have a stored value matching this criteria loaded from the database?
                    const nondbentry = self.Preferences.find(
                        p =>
                            p.EmailPreferenceOptionID === opt.EmailPreferenceOptionID &&
                            p.RoleID === role.RoleID &&
                            p.NonOwner === nonentry.NonOwner
                    );

                    if (nondbentry) {
                        nonentry.InstantEmail = nondbentry.InstantEmail;
                        nonentry.DailyRoundupEmail = nondbentry.DailyRoundupEmail;

                        // For mobile display, we need to convert the individual bit fields into a combined field.
                        if (nonentry.InstantEmail && nonentry.DailyRoundupEmail) {
                            nonentry.MobileEmail = 'B';
                        } else if (!nonentry.InstantEmail && !nonentry.DailyRoundupEmail) {
                            nonentry.MobileEmail = 'N';
                        } else {
                            nonentry.MobileEmail = nonentry.InstantEmail ? 'I' : 'D';
                        }
                        nonentry.EmailPreferenceID = nondbentry.EmailPreferenceID;
                    }

                    optarr.push(nonentry);
                }
            });

            matrix.push({
                Action: opt.Description,
                Roles: optarr,
            });
        });

        self.PreferencesMatrix = matrix;
    }

    // #endregion

    // #region Mobile Role Change

    MobileRoleChange(act: any, role: EmailPreference) {
        const self = this;

        if (!role) {
            return;
        }

        switch (role.MobileEmail) {
            case 'B':
                // Both are now selected.
                role.InstantEmail = true;
                role.DailyRoundupEmail = true;
                break;
            case 'I':
                // Only instant is selected.
                role.InstantEmail = true;
                role.DailyRoundupEmail = false;
                break;
            case 'D':
                // Only daily is selected.
                role.InstantEmail = false;
                role.DailyRoundupEmail = true;
                break;
            case 'N':
                // Neither are selected.
                role.InstantEmail = false;
                role.DailyRoundupEmail = false;
                break;
        }

        // Update the preferences in the database now.
        self.appService
            .SetEmailPreference(
                self.User.AuthToken,
                self.User.CurrentRole,
                role.EmailPreferenceID,
                role.EmailPreferenceOptionID,
                role.RoleID,
                role.NonOwner,
                role.InstantEmail,
                role.DailyRoundupEmail
            )
            .subscribe(
                result => {
                    // Splash success.
                    self.splashService.SnackbarSuccess('Email Preference Updated Successfully.');
                },
                error => self.errorService.HandleServiceError(error)
            );
    }

    // #endregion

    // #region Show Tooltip

    ShowTooltip(role: RoleHeaderInfo) {
        const self = this;

        if (!role || !role.ToolTipInfo) {
            return;
        }

        self.dialogService
            .OpenMessageBoxDialog({
                TitleText: 'Role Explanation',
                MessageText: role.ToolTipInfo,
                OKText: 'OK',
                height: '200px',
            })
            .subscribe();
    }

    // #endregion

    // #region Update Preference

    UpdatePreference(role: EmailPreference, emailtype: string) {
        const self = this;

        // Set the appropriate email type to Changed, to trigger the background shading.
        if (emailtype === 'I') {
            role.InstantChanged = true;
        } else {
            role.DailyChanged = true;
        }

        // Send off the request to the server for updating.
        self.appService
            .SetEmailPreference(
                self.User.AuthToken,
                self.User.CurrentRole,
                role.EmailPreferenceID,
                role.EmailPreferenceOptionID,
                role.RoleID,
                role.NonOwner,
                role.InstantEmail,
                role.DailyRoundupEmail
            )
            .subscribe(
                result => {
                    // Splash success.
                    self.splashService.SnackbarSuccess('Email Preference Updated Successfully.');

                    // Clear the changed flag to remove highlighting.
                    if (emailtype === 'I') {
                        role.InstantChanged = false;
                    } else {
                        role.DailyChanged = false;
                    }

                    // Resync the mobile option
                    if (role.InstantEmail && role.DailyRoundupEmail) {
                        role.MobileEmail = 'B';
                    } else if (!role.InstantEmail && !role.DailyRoundupEmail) {
                        role.MobileEmail = 'N';
                    } else {
                        role.MobileEmail = role.InstantEmail ? 'I' : 'D';
                    }
                },
                error => self.errorService.HandleServiceError(error)
            );
    }

    // #endregion
}

// #region Custom Classes

// #region Matrix Info Class

export class MatrixInfo {
    Action: string;
    Roles: EmailPreference[];
}

// #endregion

// #region Role Header Info Class

export class RoleHeaderInfo {
    RoleDescription: string;
    ToolTipInfo: string;
}

// #endregion

// #endregion
